import "../style.scss";

import { formatLongDate } from "@clipboard-health/date-time";
import { useModalState } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { IonContent, IonItem, IonList, IonListHeader, IonPage } from "@ionic/react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box, DialogContent, IconButton } from "@mui/material";
import { api } from "@src/app/api";
import { DEFAULT_TIMEZONE } from "@src/app/attendancePolicy/constants";
import { ShiftFilters } from "@src/app/components/shiftFilters";
import { useShowReviewPrompt } from "@src/app/rating/reviewPrompt/useShowReviewPrompt";
import { useSession } from "@src/app/store/helperHooks";
import {
  checkRolloutMsa,
  isNonConflictingShift,
  isShiftPriorityValid,
  removeConflictingShifts,
} from "@src/app/utils/shiftHelper";
import { useAgentPreferences } from "@src/appV2/Agents/api/useAgentPreferences";
import { useAgentProfile } from "@src/appV2/Agents/api/useAgentProfile";
import { CbhFeatureFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import {
  AppBarHeader,
  FullScreenDialog,
  PageWithHeader,
  SlideUpTransition,
  useToast,
} from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetBookabilityStatusForShifts } from "@src/appV2/OpenShifts/ShiftAction/api/useGetBookabilityStatusForShifts";
import { HCF_PROFILE_SOURCE } from "@src/constants";
import { Agent, Facility, LegacyShift, Shift } from "@src/lib/interface";
import { parseISO } from "date-fns";
import { intersection, orderBy } from "lodash";
import moment from "moment-timezone";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { USER_EVENTS } from "../../../constants/userEvents";
import InstantUpdate from "../../InstantUpdate";
import { getNotificationPreference } from "../../privacyPolicy/notificationPreference/api";
import { ReviewPrompt } from "../../rating/reviewPrompt";
import { Store } from "../../store/store.model";
import {
  addAgentActivityLog,
  fetchInstantBookShifts,
  instantPayStatus,
  isHCPInstantPayAllowed,
} from "../api";
import { HCP_ACTIONS } from "../constants";
import { UpdateShiftListFn } from "../custom-hooks/interfaces";
import { useShiftReqTag } from "../custom-hooks/useShiftReqTag";
import { FacilityDetails } from "../facilityDetails";
import { ShiftListProps } from "../model";
import { ShiftItem } from "../shiftItem";

export const shouldShiftLeadToMyDetailsPage = (shift: Shift, agent?: Agent): boolean => {
  const isAssignedToCurrentUser = shift.agentId === agent?.userId;
  const isIPAndUserClockedIn = shift.isInstantPay && shift.clockInOut?.start;
  const hasShiftEnded = moment(shift.end).isBefore(moment());
  return !!(isAssignedToCurrentUser && (isIPAndUserClockedIn || hasShiftEnded));
};

const ShiftListPage: FC<ShiftListProps> = ({
  day,
  shiftName,
  shifts,
  onClose,
  goToDocumentsPage,
  getOpenAndMyShifts,
  loadingShiftList,
  searchMode,
  distancePreference,
  qualificationPreference,
  filterDays,
  shiftSlots,
  updateShiftFields,
}) => {
  const { showErrorToast } = useToast();

  const { agent, bookingRolloutMsas } = useSession();
  const [loading, setLoading] = useState<boolean>(false);
  const [isShiftLoaded, setIsShiftLoaded] = useState<boolean>(false);

  const [sortedShifts, setSortedShifts] = useState<Shift[]>([]);
  const [facilityDetails, setFacilityDetails] = useState<Facility>();
  const [shiftDetails, setShiftDetails] = useState<Shift>();
  const [isHcpInstantPayEnabled, setIsHcpInstantPayEnabled] = useState(false);
  const [displayExtraTimePayCard, setDisplayExtraTimePayCard] = useState(false);

  const facilityDetailsModalState = useModalState();

  const [instantBookShifts, setInstantBookShifts] = useState<Shift[]>([]);
  const [pushNotification, setPushNotification] = useState<{
    [key in string]: boolean;
  }>({});
  const { isPushSet } = useSelector((state: Store) => state.notificationStore);

  const { setFilters } = useAgentPreferences();
  const { data: agentProfile } = useAgentProfile();

  // Given the probability of the FF and a random probability , this decides whether to
  // make the api call to the Main-API or the BFF-API
  const ldFlags = useCbhFlags();

  const { qualifications: allowedQualificationsForWorkerAvailability } = ldFlags[
    CbhFeatureFlag.ENABLE_WORKER_AVAILABILITY
  ] ?? {
    qualifications: [],
  };
  const agentAvailableQualifications = agent?.licensesData?.map((item) => item.qualification) ?? [];
  const isWorkerAvailabilityEnabled =
    intersection(allowedQualificationsForWorkerAvailability, agentAvailableQualifications).length >
    0;

  useLogEffect(USER_EVENTS.VIEWED_BOOK_SHIFTS);

  useEffect(() => {
    (async function () {
      const { isNotificationAlertShown } = agent as Agent;
      const response = await Promise.all([
        api.env.fetchEnvVariablesWithPushNotificationOnAccountTab(),
        getNotificationPreference(),
      ]);
      setPushNotification({
        accepted: response[1]?.data?.notificationPreferences?.push,
        pushFlag: response[0]?.PushNotificationOnAccountTab as boolean,
        isNotificationAlertShown: isNotificationAlertShown as boolean,
        isPushSet,
      });
    })();
  }, [setPushNotification, agent, isPushSet]);

  const fetchHcpInstantPayStatus = async () => {
    const status = await instantPayStatus();
    setIsHcpInstantPayEnabled(isHCPInstantPayAllowed(status));
  };

  useEffect(() => {
    fetchHcpInstantPayStatus();
  }, []);

  useEffect(() => {
    // We had a odd issue where the Shift listed on the calendar was different
    // from the selected date, ticket CH-20774. We were not able to reproduce
    // the error, this loggings can help us tracking in case it happens again.
    const allShifts = [...shifts.open, ...shifts.assigned];
    allShifts.forEach((shift) => {
      const selectedDate = moment.tz(day, DEFAULT_TIMEZONE);
      const isSameDate = selectedDate.isSame(moment(shift.start), "date");
      if (!isSameDate) {
        logEvent(USER_EVENTS.SHIFT_WRONG_DATE_SLOT, {
          error: "Shift was incorrectly displayed on a different date slot in the Calendar",
          day,
          shiftId: shift._id,
          shiftStart: shift.start,
          shiftEnd: shift.end,
          agentId: shift.agentId,
        });
      }
    });
  }, [shifts.open, shifts.assigned, day]);

  const openShiftIds = shifts.open
    .filter((shift) => isDefined(shift._id))
    .slice(0, 50) // bookability endpoint can currently only recieve a maximum of 50 shift IDs to process
    .map((shift) => shift._id ?? "");

  const {
    queryResult: bookabilityStatusQueryResult,
    shiftBookabilityById,
    isSingleSourceOfBookabilityEnabled,
  } = useGetBookabilityStatusForShifts({
    shiftIds: openShiftIds,
    agentId: agent?.userId ?? "",
  });

  const { isLoading: isLoadingShiftBookability, fetchStatus: shiftBookabilityFetchStatus } =
    bookabilityStatusQueryResult;

  const shiftSortKeyTimeslotFlag = ldFlags[CbhFeatureFlag.SHIFTS_SORT_KEY_TIMESLOT];
  useEffect(() => {
    // SHIFTS_SORT_KEY_TIMESLOT == distance: sort by distance from HCP address to HCF address, closest to farthest
    // SHIFTS_SORT_KEY_TIMESLOT == pay: sort by hourly rate, highest to lowest and then by distance , closest to farthest
    const sortIdentities = [
      (shift) => (shift.priorityTill && isShiftPriorityValid(shift.priorityTill) ? 0 : 1),
      "facility.distance",
      "start",
    ];
    const sortOrders: Array<"asc" | "desc"> = ["asc", "asc", "asc"];
    if (shiftSortKeyTimeslotFlag === "pay") {
      sortIdentities.splice(1, 0, "finalPay");
      sortOrders.splice(1, 0, "desc");
    }
    setSortedShifts(
      orderBy(
        removeConflictingShifts(shifts.open, shifts.assigned),
        sortIdentities,
        sortOrders
      ) as Shift[]
    );
  }, [shifts.open, shifts.assigned, shiftSortKeyTimeslotFlag]);

  function shiftValidatorBeforeSubmission(shift: Shift) {
    // Already assigned shifts doesn't appear on same shift type. So only checking on open shifts.
    if (
      isNonConflictingShift(
        [...shifts.assigned, ...shifts.open.filter(({ agentId }) => agentId === agent?.userId)],
        shift
      )
    ) {
      return true;
    }
    showErrorToast(
      `Unable to book shift, it looks like you're already working at that time! If there's a mistake, please contact support for help`
    );
    return false;
  }

  const history = useHistory();
  function onClickAssignedShiftItem(shift: Shift) {
    return () => onShiftCardTapped({ shift });
  }

  const updateShiftList: UpdateShiftListFn = (shift, isInstantBooking, response) => {
    if (response && !response.isAlreadyGrabbed) {
      sortedShifts.forEach((current) => {
        if (current._id === shift._id) {
          if (isInstantBooking) {
            current.agentId = agent?.userId;
            current.isHCFConfirmed = undefined;
          } else {
            if (checkRolloutMsa(shift as LegacyShift, bookingRolloutMsas as string[])) {
              current.agentId = agent?.userId;
              current.isHCFConfirmed = false;
            } else {
              current.interested?.push(agent?.userId as string);
            }
          }
        }
      });
      setSortedShifts(sortedShifts);
    } else {
      getOpenAndMyShifts?.();
    }
  };

  function onClickOnItemFacilityDetails(
    shift: Shift,
    isInstantBook: boolean,
    displayExtraTimePayCard = false
  ) {
    onShiftCardTapped({ shift, isInstantBook, displayExtraTimePayCard });
  }

  interface onShiftCardTappedOptions {
    shift: Shift;
    isInstantBook?: boolean;
    displayExtraTimePayCard?: boolean;
  }

  function onShiftCardTapped(options: onShiftCardTappedOptions) {
    const isInstantBook = isDefined(options.isInstantBook) ? options.isInstantBook : false;
    const displayExtraTimePayCard = isDefined(options.displayExtraTimePayCard)
      ? options.displayExtraTimePayCard
      : false;
    if (
      shouldShiftLeadToMyDetailsPage(options.shift, agent) &&
      options.shift.hasSentHomeRequest === false
    ) {
      navigateToMyShiftDetails(options.shift);
    } else {
      openFacilityDetails(options.shift, isInstantBook, displayExtraTimePayCard);
    }
  }

  function navigateToMyShiftDetails(shift: Shift) {
    history.push(`/home/myShifts/${shift._id!}`);
  }

  function openFacilityDetails(
    shift: Shift,
    isInstantBook: boolean,
    displayExtraTimePayCard: boolean
  ) {
    if (isInstantBook) {
      logEvent(USER_EVENTS.VIEWED_FACILITY_PROFILE, {
        bookingType: "instant book",
      });
    } else {
      logEvent(USER_EVENTS.VIEWED_FACILITY_PROFILE, {
        bookingType: "standard",
      });
    }
    setDisplayExtraTimePayCard(displayExtraTimePayCard);
    setFacilityDetails(shift.facility);
    setShiftDetails(shift);
    facilityDetailsModalState.openModal();
  }

  function closeFacilityDetails() {
    setFacilityDetails(undefined);
    setShiftDetails(undefined);
    getOpenAndMyShifts?.();
    facilityDetailsModalState.closeModal();
  }

  const logAgentActivity = useCallback(
    (shiftsToLog, action: string, shiftId = null) => {
      const agentActivityLog = {
        day,
        shiftId,
        timeSlot: shiftName.toUpperCase(),
        agentId: agent?.userId,
        shifts: shiftsToLog,
        action,
      };
      addAgentActivityLog(agentActivityLog);
    },
    [agent?.userId, day, shiftName]
  );

  useEffect(() => {
    if (!isShiftLoaded) {
      return;
    }

    let currentIndex = 1;
    const shiftsToLog = [...shifts.open, ...shifts.assigned].reduce((filtered: Shift[], option) => {
      if (isDefined(option)) {
        const shift = {
          ...option,
          position: currentIndex,
          isInstantBook: instantBookShifts.findIndex((a) => a.shiftId === option._id) > -1,
        };
        filtered.push(shift);
        currentIndex = currentIndex + 1;
      }
      return filtered;
    }, []);

    logAgentActivity(shiftsToLog, HCP_ACTIONS.TIME_SLOT_CLICK);
  }, [instantBookShifts, isShiftLoaded, logAgentActivity, shifts.assigned, shifts.open]);

  const getInstantBookShifts = useCallback(async () => {
    setLoading(true);

    const instantbookShiftsLoaded = await fetchInstantBookShifts(day);

    const instantbookShifts = (instantbookShiftsLoaded || []).filter(
      (shift) => shiftName === shift.name
    );
    setInstantBookShifts(instantbookShifts);
    setLoading(false);
    setIsShiftLoaded(true);
  }, [day, shiftName]);

  useEffect(() => {
    getInstantBookShifts();
  }, [getInstantBookShifts]);

  const [clickedInterestInShift, setClickedInterestInShift] = useState(false);

  const [openAppRatingPrompt, setOpenAppRating] = useState(true);
  const shouldShowReviewPrompt = useShowReviewPrompt(clickedInterestInShift && openAppRatingPrompt);

  function filterShiftsToLog(shiftId = null) {
    //Getting Mark Interest Shifts and filtering the null items
    const markInterestShifts = sortedShifts.filter((el) => {
      return isDefined(el) && instantBookShifts.findIndex((k) => k.shiftId === el._id) < 0;
    });
    // Combining mark and self claim shifts
    const combinedShifts = instantBookShifts.concat(markInterestShifts);
    //Using to set the position of the shift
    let currentIndex = 1;
    //filtering the mark interest shifts and is instant check
    const shiftsToLog = combinedShifts.reduce((filtered: Shift[], option) => {
      if (
        option.interested!.indexOf(agent?.userId as string) < 0 &&
        !option["isClaimed"] &&
        shiftId !== option.shiftId
      ) {
        const isInstantBook =
          instantBookShifts.findIndex((instantShift) => instantShift.shiftId === option.shiftId) >
          -1;
        const shift = {
          ...option,
          type: isInstantBook ? HCP_ACTIONS.SELF_CLAIM : HCP_ACTIONS.MARK_INTEREST,
          position: currentIndex,
          isInstantBook,
        };
        filtered.push(shift);
      }
      currentIndex = currentIndex + 1;
      return filtered;
    }, []);

    return shiftsToLog;
  }

  function shiftActivityLog(shiftId) {
    const shiftsToLog = filterShiftsToLog(shiftId);
    return { shifts: shiftsToLog };
  }

  function handleShiftActivityLog(shiftId, action) {
    const shiftsToLog = filterShiftsToLog(shiftId);
    //Recording Activity
    logAgentActivity(shiftsToLog, action, shiftId);
  }

  const [isFirstTimeInterested, setIsFirstTimeInterested] = useState<boolean>(false);
  const prevAgentInterestedAtRef = useRef<object>();

  useEffect(() => {
    prevAgentInterestedAtRef.current = agent?.interestedAt;
    /**
     * FIXME(TEV-226): This is dependent on prevAgentInterestedAtRef, not sure it it is supposed to run only on the first render
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevAgentInterestedAtRef.current === null && agent?.interestedAt) {
      setIsFirstTimeInterested(true);
    }
  }, [agent?.interestedAt]);

  const availableShiftIds = useMemo(() => {
    return sortedShifts ? sortedShifts.map((shift) => shift._id!) : [];
  }, [sortedShifts]);

  const { allowedAgentReq } = useShiftReqTag();

  return (
    <IonPage>
      <PageWithHeader
        containerVariant="without-margin"
        appBarHeader={
          <AppBarHeader
            title={formatLongDate(parseISO(day))}
            leftCta={
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="Go Back"
                title="Go Back"
                onClick={() => {
                  onClose();
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            }
          />
        }
      >
        <Box sx={{ paddingY: 1 }}>
          <ShiftFilters
            totalShifts={sortedShifts.length}
            distancePreference={distancePreference}
            qualificationPreference={qualificationPreference}
            searchMode={searchMode}
            setFilters={setFilters}
            agentLicenses={agentProfile?.licensesData ?? []}
          />
        </Box>
        {loading ||
        loadingShiftList ||
        (isSingleSourceOfBookabilityEnabled &&
          isLoadingShiftBookability &&
          shiftBookabilityFetchStatus !== "idle") ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <img
              width="250"
              height="250"
              src="/assets/gifs/loadingAvailableShifts.gif"
              alt="Loading available shifts"
            />
          </div>
        ) : (
          <IonContent>
            <div className="list-title">
              <IonListHeader style={{ fontSize: "inherit" }}>
                {shiftName.toUpperCase()} Shifts
              </IonListHeader>
            </div>
            <IonList lines="none">
              {shifts.assigned.map((shift) => (
                <ShiftItem
                  key={shift._id}
                  shift={shift}
                  shiftName={shiftName}
                  onFacilityDetailsClick={onClickOnItemFacilityDetails}
                  isHcpInstantPayEnabled={isHcpInstantPayEnabled}
                  onItemClick={onClickAssignedShiftItem(shift)}
                  shiftSlots={shiftSlots}
                />
              ))}
              {!sortedShifts.length && !shifts.assigned.length && (
                <IonItem lines="none">
                  <p style={{ paddingRight: "30px", color: "#616161" }}>
                    {`${
                      isWorkerAvailabilityEnabled
                        ? "Unfortunately, this shift is no longer available."
                        : "No shifts available."
                    }`}
                  </p>
                </IonItem>
              )}
              {sortedShifts
                .filter((shift) => isDefined(shift._id))
                .map((shift) => {
                  const shiftBookability =
                    shiftBookabilityById[shift._id ?? ""]?.attributes.bookability;

                  return (
                    <ShiftItem
                      key={shift._id}
                      shift={shift}
                      isInstantBookingShift={
                        !!instantBookShifts.find(
                          (instantShift) => instantShift.shiftId === shift.shiftId
                        )
                      }
                      shiftName={shiftName}
                      goToDocumentsPage={goToDocumentsPage}
                      updateShiftList={updateShiftList}
                      updateShiftFields={updateShiftFields}
                      onFacilityDetailsClick={onClickOnItemFacilityDetails}
                      setClickedInterestInShift={(value) => setClickedInterestInShift(value)}
                      isHcpInstantPayEnabled={isHcpInstantPayEnabled}
                      handleShiftActivityLog={handleShiftActivityLog}
                      validator={shiftValidatorBeforeSubmission}
                      pushNotification={pushNotification}
                      shiftActivityLog={shiftActivityLog}
                      searchMode={searchMode}
                      availableShiftIds={availableShiftIds}
                      filterDistance={distancePreference}
                      filterDays={filterDays}
                      shiftSlots={shiftSlots}
                      allowedAgentReq={allowedAgentReq}
                      shiftBookability={shiftBookability}
                      isSingleSourceOfBookabilityEnabled={
                        isSingleSourceOfBookabilityEnabled && isDefined(shiftBookability)
                      }
                    />
                  );
                })}
            </IonList>
          </IonContent>
        )}

        {shouldShowReviewPrompt && <ReviewPrompt onDidDismiss={() => setOpenAppRating(false)} />}

        {isDefined(facilityDetails) ? (
          <FullScreenDialog
            modalState={facilityDetailsModalState}
            TransitionComponent={SlideUpTransition}
          >
            <DialogContent sx={{ padding: 0 }}>
              <FacilityDetails
                onClose={closeFacilityDetails}
                facility={facilityDetails}
                shift={shiftDetails}
                displayExtraTimePayCard={displayExtraTimePayCard}
                hcfProfileSource={HCF_PROFILE_SOURCE.CALENDAR}
              />
            </DialogContent>
          </FullScreenDialog>
        ) : null}

        {/* If user marks interest for the first time show instant update */}
        {pushNotification.pushFlag && isFirstTimeInterested && (
          <InstantUpdate onClose={() => setIsFirstTimeInterested(false)} />
        )}
      </PageWithHeader>
    </IonPage>
  );
};

export { ShiftListPage };
